import {
  CabinCategoryID,
  IbePriceModelCode,
  MainCabinCategoryID,
  PriceModelCode,
  Surcharge,
} from '@gecko-akh/pkg-cruise-api-types/src/iris'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { filter, map, orderBy, sum } from 'lodash-es'
import {
  CabinCategory as BewotecCabinCategory,
  Cruise as BewotecCruise,
  Pricing,
  Region,
  Ship,
} from '../../../bewotec/openapi'
import { CabinMainCategoryToCabinType } from '../../../general/classes.js'
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT } from '../../../general/helper.js'
import {
  CABIN_MAIN_CATEGORY_TO_CABIN_TYPE,
  CLASSIC,
  CLASSICAI,
  COMFORTAI,
  IND,
  INDAI,
  LIGHT,
  PAUSCHAL,
  PAUSCHALAI,
} from '../../../general/master-data.js'
import { CabinCategory, IDetailDeck } from '../cabin/interfaces.js'
import { DetailCruiseExtras } from './classes/DetailCruiseExtras.js'
import { DetailFlightPrices } from './classes/DetailFlightPrices.js'
import { Days2Segments, IDetailCruise, IDetailRoute, IDetailShip } from './interfaces.js'

dayjs.locale('de')

export const initCruise = (bewotecCruise: BewotecCruise): IDetailCruise => {
  const isSelection = !!bewotecCruise.cruiseAttributes?.find(attribute =>
    attribute.attributeValue?.includes('selection')
  )

  const cruiseCampaigns = bewotecCruise.priceModel?.campaigns
  let campaignEnd: string = ''

  if (!!cruiseCampaigns?.length) {
    for (const campaign of cruiseCampaigns) {
      if (!!campaign.endDate) {
        campaignEnd = campaign.endDate
        break
      }
    }
  }

  const cruise: IDetailCruise = {
    id: bewotecCruise.cruiseId ?? '',
    duration: bewotecCruise.duration ?? 0,
    startDate: !!bewotecCruise.departureDate ? dayjs(bewotecCruise.departureDate).format(DEFAULT_DATE_FORMAT) : '',
    endDate: !!bewotecCruise.arrivalDate ? dayjs(bewotecCruise.arrivalDate).format(DEFAULT_DATE_FORMAT) : '',
    isAidaSelection: isSelection,
    campaignEnd,
    regions: getRegions(bewotecCruise.regions),
    hasFlexFlights: !!bewotecCruise.flexFlight,
  }

  const checkInTimes = bewotecCruise.checkinTime

  if (!!checkInTimes) {
    cruise.checkInTimes = {
      start: dayjs(checkInTimes.startDateTime).format(DEFAULT_TIME_FORMAT) ?? '',
      end: dayjs(checkInTimes.endDateTime).format(DEFAULT_TIME_FORMAT) ?? '',
    }
  }

  return cruise
}

const getRegions = (regions: Region[] | undefined) => {
  const regionNames = []

  if (!regions?.length) return ''

  for (const region of regions) {
    regionNames.push(region.name)
  }

  return regionNames.join(';')
}

export const initShip = (bewotecShip: Ship): IDetailShip => {
  return {
    id: bewotecShip.code ?? '',
    name: bewotecShip.name ?? '',
    configCode: bewotecShip.configCode,
    teaserImageUrl: toAdobeJpegImageUrl(bewotecShip?.imageUrl),
  }
}

const toAdobeJpegImageUrl = (imageUrl: string | undefined) => {
  return !!imageUrl ? `${imageUrl.replace('?', ':16-9?')}&wid=640&hei=360&bfc=off` : ''
}

export const initRoute = (cruise: BewotecCruise) => {
  const itinerary = cruise.itinerary!
  const segments = itinerary?.segments
  const filteredSegments = filter(segments, segment => segment.port?.code !== 'XXSEE')

  const route: IDetailRoute = {
    id: itinerary.code ?? '',
    name: itinerary.name ?? '',
    mapImageUrl: !!itinerary.imageUrl ? itinerary.imageUrl.replace('https://aida.de', 'https://my.aida.de') : '',
    germanDeparture: segments?.[0].port?.countryCode === 'DE',
    germanArrival: segments?.[segments.length - 1].port?.countryCode === 'DE',
    portIds: map(filteredSegments, segment => segment.port?.code ?? ''),
    ports: map(filteredSegments, segment => segment.port?.name ?? ''),
    segments: routeSegments(cruise),
  }

  return route
}

const routeSegments = (cruise: BewotecCruise) => {
  const segments = cruise.itinerary?.segments
  const days2Segments: Days2Segments = new Array(segments?.length)

  if (!segments?.length) return []

  for (let i = 0; i < segments.length; ++i) {
    const segment = segments![i]
    const port = segment.port
    const date = !!segment.date ? dayjs(segment.date).format(DEFAULT_DATE_FORMAT) : ''

    if (segment.port?.name === 'SEE') {
      days2Segments[i] = { ocean: true, day: date }
    } else {
      days2Segments[i] = {
        portName: port?.name ?? '',
        country: port?.countryName && port.countryCode !== 'XX' ? port?.countryName : '',
        arrivalTime: !!segment.arrivalDateTime ? dayjs(segment.arrivalDateTime).format(DEFAULT_TIME_FORMAT) : '',
        departureTime: !!segment.departureDateTime ? dayjs(segment.departureDateTime).format(DEFAULT_TIME_FORMAT) : '',
        day: date,
      }
    }
  }

  return days2Segments
}

export const initCategories = (cruise: BewotecCruise, shipId: string): CabinCategory[] => {
  const mappedCategories: CabinCategory[] = cruise.cabinCategories!.map((category: BewotecCabinCategory) => {
    const pricingArray = initCategoryPricings(
      cruise.priceModel!.code,
      category.pricings,
      cruise.bestPriceFlight?.pricings,
      cruise.priceModel?.mandatoryFlight
    )

    return {
      available: !!category.available,
      id: category.code as CabinCategoryID,
      mainID: category.mainCategoryCode as MainCabinCategoryID,
      priceModel: cruise.priceModel!.code as IbePriceModelCode, // TODO: check if this is mandatory
      price: sum(pricingArray),
      mainName: category.mainCategoryName ?? '',
      image: toAdobeJpegImageUrl(category?.previewImageUrl),
      amenities: !!category.amenities?.length ? category.amenities : [],
      surcharges: !!category.pricings?.length ? initSurcharges(category.pricings) : [],
      pricings: pricingArray,
      name: category.name ?? '',
      head: category.label ?? '',
      type: category.mainCategoryCode
        ? CABIN_MAIN_CATEGORY_TO_CABIN_TYPE[shipId as keyof CabinMainCategoryToCabinType][
            category.mainCategoryCode as MainCabinCategoryID
          ]
        : '',
    }
  })

  return orderBy(mappedCategories, 'price')
}

export const initCategoryPricings = (
  priceModel: string | undefined,
  bewotecCategoryPricings: Pricing[] | undefined,
  bewotecFlightPricings: Pricing[] | undefined,
  isMandatoryFlight?: boolean
): number[] => {
  const categoryPricings = !!bewotecCategoryPricings?.length
    ? bewotecCategoryPricings.map(pricing => pricing.amount ?? 0)
    : []

  // if the price model is one of the flight-inclusive models, we need to add the flight prices to the category prices
  if (!!priceModel && [PAUSCHAL, PAUSCHALAI].includes(priceModel) && !!isMandatoryFlight && !!bewotecFlightPricings?.length) {
    const categoryFlightPricings = categoryPricings.map((pricing, index) => pricing + (bewotecFlightPricings[index]?.amount ?? 0))

    return categoryFlightPricings
  }

  return categoryPricings
}

export const initDecks = (ship: Ship): IDetailDeck[] => {
  if (!ship.decks?.length) return []

  const decks = ship.decks.map(deck => {
    const detailDeck: IDetailDeck = {
      id: deck.deck ?? null,
      available: !!deck.deck && !!deck.imageUrl,
      imageUrl: deck.imageUrl ?? '',
    }

    return detailDeck
  })

  return decks
}

export const initCheapestFlightPrices = (cruise: BewotecCruise): DetailFlightPrices => {
  const prices = new DetailFlightPrices()

  for (const priceModel in prices) {
    if (cruise.priceModel?.code === priceModel && !!cruise.bestPriceFlight) {
      prices[priceModel as keyof DetailFlightPrices] = cruise.bestPriceFlight.pricings.reduce(
        (accumulator, pricing) => accumulator + (pricing.amount ?? 0),
        0
      )
    }
  }

  return prices
}

export const initExtras = (cruise: BewotecCruise): DetailCruiseExtras => {
  // TODO: make this prettier
  return new DetailCruiseExtras(cruise)
}

export const convertIBEPriceModel = (priceModel: IbePriceModelCode): PriceModelCode => {
  switch (priceModel) {
    case COMFORTAI:
      return 'COMFORTAI'
    case IND:
      return 'PREMIUM'
    case INDAI:
      return 'PREMIUMAI'
    case CLASSIC:
      return 'CLASSIC'
    case CLASSICAI:
      return 'CLASSICAI'
    case LIGHT:
      return 'LIGHT'
    case PAUSCHALAI:
      return 'PAUSCHALAI'
    case PAUSCHAL:
      return 'PAUSCHAL'
    default:
      return priceModel
  }
}

const initSurcharges = (categoryPricings: Pricing[]): Surcharge[] => {
  return categoryPricings.flatMap(pricing => {
    if (!pricing.surcharges?.length) {
      return []
    }

    return pricing.surcharges.map(surcharge => {
      const mappedSurcharge: Surcharge = {
        amount: surcharge.amount ?? 0,
        type: surcharge.code ?? '',
      }

      return mappedSurcharge
    })
  })
}

export const cruiseHelper = {
  initCategories,
  initCheapestFlightPrices,
  initCruise,
  initDecks,
  initRoute,
  initShip,
  initExtras,
}

// ---------- export for testing purposes only ----------

export const exportForTesting = {
  getRegions,
  initSurcharges,
  routeSegments,
  toAdobeJpegImageUrl,
}
